import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import SectionHeader from "@/components/feature/SectionHeader";
import { animeList } from "@/mocks/anime";

export default function AnimeDetail() {
  const { id } = useParams();
  const anime = animeList.find((a) => a.id === id) ?? animeList[0];
  const related = animeList.filter((a) => a.category === anime.category && a.id !== anime.id).slice(0, 4);
  const finalRelated = related.length >= 1 ? related : animeList.filter((a) => a.id !== anime.id).slice(0, 4);

  const whyList = [
    { icon: "ri-timer-line", title: "单集够短", desc: `单集约 ${anime.episodeMin} 分钟，泡个面就能看完一集，碎片时间也能轻松追完。` },
    { icon: "ri-emotion-laugh-line", title: "笑点密集", desc: "节奏明快、包袱不断，一口气连刷数集毫无负担，快乐翻倍不占时间。" },
    { icon: "ri-moon-line", title: "轻松下饭", desc: "剧情轻松不烧脑，无论通勤还是干饭都能随时开刷，是公认的摸鱼下饭良品。" },
  ];

  return (
    <>
      <Navbar />
      <main>
        <section className="pt-24 md:pt-28">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6 py-8 md:py-12">
            <nav aria-label="面包屑" className="text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-700">首页</Link>
              <span className="mx-1.5">/</span>
              <Link to="/library" className="hover:text-primary-700">番剧库</Link>
              <span className="mx-1.5">/</span>
              <span className="text-foreground-700">{anime.name}</span>
            </nav>

            <div className="mt-6 grid gap-8 lg:grid-cols-[320px_1fr]">
              <div className="mx-auto w-56 lg:w-full">
                <div className="overflow-hidden rounded-2xl border border-background-200/70">
                  <img src={anime.cover} alt={`${anime.name} 海报封面`} className="h-auto w-full object-cover object-top" />
                </div>
              </div>

              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">{anime.category}</span>
                  <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">{anime.year} 年</span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-800">
                    <i className="ri-star-fill" aria-hidden="true" />
                    {anime.rating.toFixed(1)} 分
                  </span>
                </div>

                <h1 className="mt-4 font-heading text-3xl text-foreground-950 md:text-4xl">{anime.name}</h1>

                <div className="mt-4 grid grid-cols-3 gap-3 sm:w-96">
                  <div className="rounded-xl bg-background-100 p-3 text-center">
                    <p className="font-heading text-xl text-foreground-950">{anime.episodeCount}</p>
                    <p className="mt-0.5 text-xs text-foreground-500">总集数</p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-3 text-center">
                    <p className="font-heading text-xl text-foreground-950">{anime.episodeMin}</p>
                    <p className="mt-0.5 text-xs text-foreground-500">单集分钟</p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-3 text-center">
                    <p className="font-heading text-xl text-foreground-950">{anime.episodeCount * anime.episodeMin}</p>
                    <p className="mt-0.5 text-xs text-foreground-500">全季分钟</p>
                  </div>
                </div>

                <p className="mt-5 max-w-2xl text-sm leading-7 text-foreground-700 md:text-base">{anime.desc}</p>

                <div className="mt-5 flex flex-wrap gap-1.5">
                  {anime.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/library"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-movie-2-line" aria-hidden="true" />
                    返回番剧库挑一部
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* why */}
        <section className="bg-background-100/70">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6 py-12 md:py-16">
            <SectionHeader eyebrow="为什么值得看" title={`为什么《${anime.name}》值得一口气看完`} />
            <div className="grid gap-4 md:grid-cols-3">
              {whyList.map((w) => (
                <div key={w.title} className="rounded-2xl border border-background-200/70 bg-background-50 p-5">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent-100 text-xl text-accent-700">
                    <i className={w.icon} aria-hidden="true" />
                  </span>
                  <h3 className="mt-3 font-heading text-lg text-foreground-950">{w.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* related */}
        <section className="mx-auto w-full max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <SectionHeader eyebrow="同类推荐" title="如果你喜欢这一部" moreTo="/library" moreLabel="去番剧库看看" />
          <div className="grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4">
            {finalRelated.map((a) => (
              <AnimeCard key={a.id} anime={a} />
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}